// Section 24.6: the content registries. Each registry is built from a config (entries are
// factories reading their constants from it), so a save's config snapshot drives content
// numbers too (ADR-0008). `registry` is built from the current config; `registryFor(cfg)`
// builds and caches one for any other config. Id unions derived from the registries equal
// the core id unions (checked below at compile time).
import { config, type Config } from '../config';
import { createPipeline, type Contributor } from '../core/modifiers';
import { contributorOf, type Registry } from '../core/registry';
import type { BuildingTypeId, EventId, IllnessId, JobId, NormId, TraitId } from '../core/types';
import { buildBuildings } from './buildings';
import { ageContributor } from './contributors/age';
import { contagionContributor } from './contributors/contagion';
import { crimeContributor } from './contributors/crime';
import { eventsContributor } from './contributors/events';
import { landContributor } from './contributors/land';
import { needsContributor } from './contributors/needs';
import { seasonContributor } from './contributors/season';
import { skillsContributor } from './contributors/skills';
import { toolsContributor } from './contributors/tools';
import { buildEvents } from './events';
import { buildIllnesses } from './illnesses';
import { buildJobs } from './jobs';
import { names } from './names';
import { buildNorms } from './norms';
import { buildPolicies } from './policies';
import { buildPriorityGroups } from './priorityGroups';
import { buildRationPolicies } from './rationPolicies';
import { buildTraits } from './traits';

export { names };

export type JobKey = keyof ReturnType<typeof buildJobs>;
export type BuildingKey = keyof ReturnType<typeof buildBuildings>;
export type EventKey = keyof ReturnType<typeof buildEvents>;
export type TraitKey = keyof ReturnType<typeof buildTraits>;
export type IllnessKey = keyof ReturnType<typeof buildIllnesses>;
export type NormKey = keyof ReturnType<typeof buildNorms>;

// The registry keys and the config-derived id unions must be the same sets.
type Same<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;
export const REGISTRY_IDS_MATCH_CONFIG: [
  Same<JobKey, JobId>,
  Same<BuildingKey, BuildingTypeId>,
  Same<EventKey, EventId>,
  Same<TraitKey, TraitId>,
  Same<IllnessKey, IllnessId>,
  Same<NormKey, NormId>,
] = [true, true, true, true, true, true];

/** Core contributors in fixed slot order (season, land, needs, age, illnesses, skills, tools, events). */
export const coreContributors: readonly Contributor[] = Object.freeze([
  seasonContributor,
  landContributor,
  needsContributor,
  crimeContributor,
  ageContributor,
  contagionContributor,
  skillsContributor,
  toolsContributor,
  eventsContributor,
]);

export function buildRegistry(cfg: Config): Registry {
  const jobs = buildJobs(cfg);
  const buildings = buildBuildings(cfg);
  const events = buildEvents(cfg);
  const policies = buildPolicies(cfg);
  const traits = buildTraits(cfg);
  const illnesses = buildIllnesses(cfg);
  const norms = buildNorms(cfg);
  const contributors = [
    ...coreContributors,
    ...Object.values(illnesses).map((d) => contributorOf('illnesses', 'illness', d)),
    ...Object.values(traits).map((d) => contributorOf('traits', 'trait', d)),
    ...Object.values(jobs).map((d) => contributorOf('jobs', 'job', d)),
    ...Object.values(buildings).map((d) => contributorOf('buildings', 'building', d)),
    ...Object.values(policies).map((d) => contributorOf('policies', 'policy', d)),
    ...Object.values(norms).map((d) => contributorOf('norms', 'norm', d)),
  ].filter((c) => c.targets.length > 0);
  return Object.freeze({
    jobs,
    buildings,
    events,
    policies,
    traits,
    illnesses,
    norms,
    priorityGroups: buildPriorityGroups(cfg),
    rationPolicies: buildRationPolicies(cfg),
    contributors: coreContributors,
    pipeline: createPipeline(contributors),
  });
}

/** The registry for the current config. */
export const registry: Registry = buildRegistry(config);

const byConfig = new WeakMap<Config, Registry>([[config, registry]]);

/** The registry for a given config (a save's snapshot, a test harness), built once and cached. */
export function registryFor(cfg: Config): Registry {
  let found = byConfig.get(cfg);
  if (found === undefined) {
    found = buildRegistry(cfg);
    byConfig.set(cfg, found);
  }
  return found;
}
